// SDK tipado do cliente para o BFF. Envolve fetchAutenticado (injeta Bearer),
// desempacota o envelope { ok, data } e lança ApiError em caso de erro.
// É a porta única do front para o backend — substitui o `.from(...)` direto.
import { fetchAutenticado } from '../fetchAutenticado';
import { getSessao } from '../auth';

type ApiOk<T> = { ok: true; data: T };
type ApiErr = { ok: false; error: { code: string; message: string; details?: unknown } };

export class ApiError extends Error {
  code: string;
  details?: unknown;
  constructor(code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.details = details;
  }
}

// "Failed to fetch" (Chrome), "Load failed" (Safari), "NetworkError" (Firefox): a resposta nem chegou — a conexão
// caiu, o computador dormiu ou o servidor cortou no meio. Leitura (GET) tenta de novo sozinha até 2 vezes; gravação
// não repete (poderia duplicar) e vira uma mensagem em português dizendo o que fazer.
const ERRO_REDE = /failed to fetch|load failed|networkerror|network request failed/i;
const esperar = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function chamar(method: string, path: string, body?: unknown): Promise<Response> {
  const tentativas = method === 'GET' ? 3 : 1;
  for (let i = 0; ; i++) {
    try {
      return await fetchAutenticado(path, { method, ...(body !== undefined ? { body: JSON.stringify(body) } : {}) });
    } catch (e) {
      if (!(e instanceof TypeError) || !ERRO_REDE.test(e.message)) throw e;
      if (i + 1 < tentativas) { await esperar(i === 0 ? 800 : 2000); continue; }
      throw new ApiError('SEM_CONEXAO', method === 'GET'
        ? 'Não deu para falar com o Portal agora (a conexão caiu). Confira a internet e tente de novo.'
        : 'A conexão caiu antes da resposta. Recarregue a página e confira se ficou salvo antes de tentar de novo.');
    }
  }
}

// A sessão do Portal dura 24 h. Com a tela aberta de um dia para o outro, o pedido sairia sem login ("Token de
// autenticação ausente"): vira uma mensagem que diz como salvar sem perder o que foi digitado.
const SESSAO_VENCIDA = 'Sua sessão no Portal venceu (dura 24 horas). Para não perder o que está na tela: abra o Portal em outra aba, entre de novo e volte aqui para clicar outra vez.';

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  if (!getSessao()) throw new ApiError('SESSAO_VENCIDA', SESSAO_VENCIDA);
  const res = await chamar(method, path, body);

  let json: unknown;
  try {
    json = await res.json();
  } catch {
    throw new ApiError('PARSE_ERROR', `Resposta inválida do servidor (HTTP ${res.status})`);
  }

  // Nunca assumir o formato do corpo: uma rota que responda fora do envelope
  // { ok, data } derrubava isto com "undefined is not an object (json.error.code)",
  // escondendo o erro de verdade. Aqui a resposta malformada vira uma mensagem
  // legível — e o request só é dado como bom se o envelope estiver mesmo lá.
  const corpo = json as Partial<ApiOk<T>> & Partial<ApiErr>;

  if (corpo?.ok === true) return corpo.data as T;

  if (res.status === 401 && corpo?.error?.code === 'UNAUTHENTICATED') throw new ApiError('SESSAO_VENCIDA', SESSAO_VENCIDA);

  if (corpo?.ok === false && corpo.error) {
    throw new ApiError(corpo.error.code, corpo.error.message, corpo.error.details);
  }

  throw new ApiError(
    'RESPOSTA_INESPERADA',
    `O servidor respondeu fora do formato esperado (HTTP ${res.status}) em ${method} ${path}.`,
    json,
  );
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
};
